import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { mediaQueryLarge, onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('keydown', this.#onDrillTab);
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onDrillTab);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.#releaseDrill();
  }

  /**
   * Close the drill-down drawer when the viewport reaches the desktop breakpoint, where it is hidden
   * @param {MediaQueryListEvent} event
   */
  #onBreakpointChange = (event) => {
    if (event.matches && this.isOpen && this.#isDrill()) this.close();
  };

  /** @type {HTMLElement | null} */
  #drillTrap = null;

  /**
   * Move Tab and Shift+Tab through the rendered controls of the open drill-down panel and of the menu footer,
   * because the native trap counts the hidden controls of the closed panels nested in it
   * and the covered level-0 items sit between a submenu panel and the footer in the DOM
   * @param {KeyboardEvent} event
   */
  #onDrillTab = (event) => {
    if (event.key !== 'Tab' || !this.#drillTrap || !this.#isDrill()) return;

    const focusable = this.#drillFocusable();
    if (!focusable.length) return;

    event.preventDefault();
    const index = focusable.findIndex((element) => element === document.activeElement);
    const step = event.shiftKey ? -1 : 1;
    const next = index === -1 ? focusable.at(event.shiftKey ? -1 : 0) : focusable.at((index + step) % focusable.length);
    if (next instanceof HTMLElement) next.focus();
  };

  /**
   * Keep focus inside the open drill-down panel and the menu footer
   * @param {FocusEvent} event
   */
  #onDrillFocusIn = (event) => {
    if (!this.#drillTrap || !(event.target instanceof Node)) return;

    const target = event.target;
    if (this.#drillRegions().some((region) => region.contains(target)) || this.#drillHeaderClose() === target) return;

    const first = this.#drillFocusable()[0];
    if (!(first instanceof HTMLElement)) return;

    event.stopPropagation();
    first.focus();
  };

  /**
   * The visible panel of the open level plus the menu footer, which stays visible below the navigation panels
   * (the region and language panel lives in the footer and covers it)
   * @returns {HTMLElement[]}
   */
  #drillRegions() {
    if (!this.#drillTrap) return [];

    const footer = this.refs.menuDrawer.querySelector('.menu-drawer__utility-links');
    if (this.#drillTrap === this.refs.menuDrawer || !(footer instanceof HTMLElement) || footer.contains(this.#drillTrap)) {
      return [this.#drillTrap];
    }

    return [this.#drillTrap, footer];
  }

  /**
   * The close button of the drawer header while a panel is open, since the panel's back button shares its row
   * @returns {HTMLElement | null}
   */
  #drillHeaderClose() {
    if (!this.#drillTrap || this.#drillTrap === this.refs.menuDrawer) return null;

    const close = this.refs.menuDrawer.querySelector('.eme-menu-drawer__header .menu-drawer__close-button');
    return close instanceof HTMLElement && isRendered(close) ? close : null;
  }

  /**
   * @returns {HTMLElement[]} The rendered controls of the drill-down regions, panel first,
   * with the header close button right after the panel's back button
   */
  #drillFocusable() {
    const focusable = new Set();
    for (const region of this.#drillRegions()) {
      for (const element of region.querySelectorAll(DRILL_FOCUSABLE)) {
        if (element instanceof HTMLElement && isRendered(element)) focusable.add(element);
      }
    }
    const ordered = Array.from(focusable);
    const close = this.#drillHeaderClose();
    if (!close) return ordered;

    const back = ordered.findIndex((element) => element.classList.contains('menu-drawer__back-button'));
    if (back === -1) return ordered;
    ordered.splice(back + 1, 0, close);
    return ordered;
  }

  /**
   * @param {HTMLElement} panel
   */
  #trapDrill(panel) {
    removeTrapFocus();
    this.#drillTrap = panel;
    document.addEventListener('focusin', this.#onDrillFocusIn, true);
  }

  #releaseDrill() {
    this.#drillTrap = null;
    document.removeEventListener('focusin', this.#onDrillFocusIn, true);
  }

  /**
   * The deepest drill-down level open in the navigation, or the root
   * @returns {HTMLDetailsElement}
   */
  #openLevel() {
    const open = Array.from(this.refs.menuDrawer.querySelectorAll('.menu-drawer__navigation details.menu-open')).at(-1);
    return open instanceof HTMLDetailsElement ? open : this.refs.details;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    if (!this.#isDrill()) {
      this.#close(this.#getDetailsElement(event));
      return;
    }

    const openDetails = event.target instanceof Element ? event.target.closest('details.menu-open') : null;
    this.#close(openDetails instanceof HTMLDetailsElement && openDetails !== this.refs.details ? openDetails : this.#openLevel());
  };

  /**
   * @returns {boolean} Whether the drawer uses the ÈME full-screen drill-down mode
   */
  #isDrill() {
    return this.refs.menuDrawer.classList.contains('eme-menu-drawer');
  }

  /**
   * The visible panel of a level in drill-down mode, used to trap focus
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement}
   */
  #drillPanel(details) {
    if (details === this.refs.details) return this.refs.menuDrawer;

    return details.querySelector(':scope > .menu-drawer__submenu') ?? details;
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      this.#syncMobileLayout();
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (!this.#isDrill()) {
            trapFocus(details);
            return;
          }

          const panel = this.#drillPanel(details);
          this.#trapDrill(panel);
          const initialFocus =
            details === this.refs.details
              ? panel.querySelector('.eme-menu-drawer__header .menu-drawer__close-button')
              : panel.querySelector('.menu-drawer__back-button');
          if (initialFocus instanceof HTMLElement) initialFocus.focus();
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    const submenuStillOpen =
      this.#isDrill() && details !== this.refs.details && this.refs.menuDrawer.querySelector('details.menu-open');
    if (!submenuStillOpen) this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#releaseDrill();
          if (this.#isDrill()) summary.focus();
        } else if (this.#isDrill()) {
          const parentDetails = details.parentElement?.closest('details.menu-open');
          const parentLevel =
            parentDetails instanceof HTMLDetailsElement && parentDetails !== this.refs.details
              ? parentDetails
              : this.#openLevel();
          this.#trapDrill(this.#drillPanel(parentLevel));
          summary.focus();
        } else {
          trapFocus(this.refs.details);
        }
        this.#syncMobileLayout();
      },
      { subtree: false }
    );
  }

  /**
   * Expose the footer height, so the drill-down panels end above the menu footer
   */
  #syncMobileLayout() {
    if (!this.#isDrill()) return;

    const footer = this.refs.menuDrawer.querySelector('.menu-drawer__utility-links');
    if (footer instanceof HTMLElement) {
      this.refs.menuDrawer.style.setProperty('--eme-menu-footer-height', `${footer.offsetHeight}px`);
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

const DRILL_FOCUSABLE =
  "summary, a[href], button:enabled, select:enabled, input:not([type=hidden]):enabled, textarea:enabled, [tabindex]:not([tabindex^='-'])";

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isRendered(element) {
  if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true });

  return element.getClientRects().length > 0;
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
